import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { shopApi, tagApi } from '@app/store';
import { IShop, ITag, ShopDTO } from '@app/common';
import { extractData } from '@app/helpers/utils';
import { Button, Form, Panel } from '@app/components';
import { ShopForm } from './ShopForm';
import {
  createDefaultCapacityForDay,
  ShopFormValues,
  transformShopFormValuesToPayload,
} from './shop-form.helpers';
import { useToast, useTriptych, useUpdateCardData } from '@app/hooks';
import { CheckPermissions } from '@app/components/CheckPermissions';

const transformToUpdatableFormShop = (shop?: Partial<IShop>, tags: ITag[] = []): ShopFormValues => {
  return {
    publicName: shop?.publicName,
    internalName: shop?.internalName,
    description: shop?.description,
    address: shop?.address,
    siret: shop?.siret,
    // Identité fiscale (NF525 R13 10, 11, 14, 15). `?? ''` et non la valeur
    // brute : des milliers de fiches n'ont aucun de ces champs, et un
    // `undefined` rendrait l'entrée non contrôlée puis contrôlée à la saisie.
    zipCode: shop?.zipCode ?? '',
    city: shop?.city ?? '',
    naf: shop?.naf ?? '',
    vatNumber: shop?.vatNumber ?? '',
    phone: shop?.phone,
    email: shop?.email,
    disabled: shop?.disabled,
    location: shop?.location ?? {
      type: 'Point',
      coordinates: [0.6792692, 47.4073735],
    },
    eshopMessage: shop?.eshopMessage ?? '',
    takeAwaySchedule: shop?.takeAwaySchedule,
    deliverySchedule: shop?.deliverySchedule,
    platformsSchedule: shop?.platformsSchedule,
    takeAwayTime: shop?.takeAwayTime || 15,
    deliveryTime: shop?.deliveryTime || 15,
    isDeliveryOpen: !!shop?.isDeliveryOpen,
    isTakeawayOpen: !!shop?.isTakeawayOpen,
    enableDeliveryTracking: !!shop?.enableDeliveryTracking,
    socialMediaLinks: shop?.socialMediaLinks,
    productionCapacities: shop?.productionCapacities?.map((productionCapacity) => ({
      ...productionCapacity,
      tags: (productionCapacity.tagsIds || [])
        .map((tagId) => tags.find((tag) => tag._id === tagId))
        .filter((tag): tag is ITag => !!tag),
      capacity: {
        monday: createDefaultCapacityForDay(shop as IShop, 'monday', productionCapacity.capacity),
        tuesday: createDefaultCapacityForDay(shop as IShop, 'tuesday', productionCapacity.capacity),
        wednesday: createDefaultCapacityForDay(
          shop as IShop,
          'wednesday',
          productionCapacity.capacity,
        ),
        thursday: createDefaultCapacityForDay(
          shop as IShop,
          'thursday',
          productionCapacity.capacity,
        ),
        friday: createDefaultCapacityForDay(shop as IShop, 'friday', productionCapacity.capacity),
        saturday: createDefaultCapacityForDay(
          shop as IShop,
          'saturday',
          productionCapacity.capacity,
        ),
        sunday: createDefaultCapacityForDay(shop as IShop, 'sunday', productionCapacity.capacity),
      },
    })),
  };
};

interface ShopUpdateProps {
  shop?: IShop;
  isFetching?: boolean;
  onDelete: (product: Partial<IShop>) => void;
}

export const ShopUpdate = ({ shop, isFetching, onDelete }: ShopUpdateProps) => {
  const toast = useToast();

  const triptych = useTriptych();

  const [update, { isLoading, error: apiErrors }] = shopApi.useUpdateOneShopsMutation();

  const productionCapacitiesTagsIds = useMemo(
    () => [...new Set((shop?.productionCapacities || []).flatMap((productionCapacity) => productionCapacity.tagsIds || []))],
    [shop],
  );

  const { data: paginatedTags, isFetching: isFetchingTags } = tagApi.useReadManyTagQuery({
    filters: {
      _id: {
        $in: productionCapacitiesTagsIds
      }
    },
  }, { skip: !productionCapacitiesTagsIds.length });

  const tags = useMemo(() => extractData(paginatedTags), [paginatedTags]);

  const formMethods = useForm<ShopFormValues>({
    resolver: classValidatorResolver(ShopDTO),
    defaultValues: transformToUpdatableFormShop(shop, tags),
  });

  // @ts-ignore
  useUpdateCardData(formMethods, ['publicName']);

  useEffect(() => {
    formMethods.reset(transformToUpdatableFormShop(shop, tags));
  }, [shop, tags, formMethods.reset]);

  const onUpdate = async (formValues: ShopFormValues) => {
    try {
      const updatedShop = await update(
        transformShopFormValuesToPayload({ _id: shop?._id, ...formValues }),
      ).unwrap();

      triptych.close({ mode: 'UPDATE', data: updatedShop });

      toast.success('Modification effectuée avec succès.');
    } catch {
      toast.error('Une erreur est survenue lors de la modification.');
    }
  };

  return (
    <Panel.Content>
      <Form isLoading={isFetching || isFetchingTags} formMethods={formMethods} apiErrors={apiErrors}>
        <Panel.FormContainer>
          <CheckPermissions permissions={['dashboard::shops.delete']}>
            <Panel.Header>
              <Button title="Supprimer" onPress={() => onDelete(shop!)} bordered important />
            </Panel.Header>
          </CheckPermissions>

          <ShopForm shop={shop!} />
        </Panel.FormContainer>

        <Panel.Actions>
          <Button title="Retour" grow large bordered onPress={() => triptych.close()} />

          <Button
            loading={isLoading}
            onPress={formMethods.handleSubmit(onUpdate)}
            title="Valider"
            grow
            large
          />
        </Panel.Actions>
      </Form>
    </Panel.Content>
  );
};
